import type { ECharts } from 'echarts/core'
import { observeEchartsResize } from './observe-echarts-resize'

// Owns the ECharts lifecycle: lazy chunk load, init, resize and dispose. It
// knows nothing about data, options or interactions. The part that is easy to
// get wrong is a dispose() that arrives while echarts is still loading.
export class EchartsHost {
  private chart: ECharts | null = null
  private stopResize: (() => void) | null = null
  private disposed = false

  // Returns `null` if dispose() ran first, so a caller that disconnected
  // mid-fetch or mid-load stops with no dead chart and no leaked observer.
  // `beforeInit` runs after the load and before echarts measures `element`, for
  // example to show a `display:none` container. It does not run on the null path.
  async mount(
    element: HTMLElement,
    beforeInit?: () => void
  ): Promise<ECharts | null> {
    const { echarts } = await import(
      /* webpackChunkName: "echarts" */ '@app/shared/echarts'
    )
    if (this.disposed) return null

    beforeInit?.()
    this.chart = echarts.init(element)
    this.stopResize = observeEchartsResize(this.chart, element)
    return this.chart
  }

  get instance(): ECharts | null {
    return this.chart
  }

  dispose(): void {
    this.disposed = true
    this.stopResize?.()
    this.stopResize = null
    this.chart?.dispose()
    this.chart = null
  }
}
